import { toast, ToastOptions } from 'react-toastify';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import { GraphQLData } from '../types/graphql';

const MessageAnchor = styled.a`
  color: inherit;
  text-decoration: underline;
`;

// Better error message for timestamp mismatch:
const getTimestampMismatchMessage = (count = 1) => (
  <span>
    This item has new changes.{' '}
    <MessageAnchor href={window.location.href}>Reload</MessageAnchor> and try
    your changes again. {count > 1 && `(${count} errors)`}
  </span>
);

// Better error message for GraphQL network error:
const NETWORK_ERROR_MESSAGE =
  'There was a network error. Your changes may not be saved.';

const INVALID_GEOMETRY_MESSAGE =
  'Please import the features again or contact support for assistance.';

// Track how many of each error have occurred (resets when toast is dismissed)
const errorCounts: Record<string, number> = {};

/**
 * Display an error toast for this error.
 *
 * Also calls `console.error()` if `logError` is true.
 */
export const showError = (
  error: unknown,
  { logError } = { logError: true },
) => {
  if (logError) console.error(error);

  const errors = Array.isArray(error) ? error : [error];
  const errorMessages = errors.map((err) => {
    try {
      if (typeof err === 'string') return err;
      if (err instanceof ApolloError) {
        if (err.networkError) {
          return NETWORK_ERROR_MESSAGE;
        }
        return err.message;
      }
      if (err instanceof Error) {
        return err.message ?? err.toString();
      }
      try {
        if ('message' in err) return err.message;
      } catch {
        // Catch these errors separately to default to return toString()
      }
      return err.toString();
    } catch {
      return 'Unknown error';
    }
  });

  let message = errorMessages.join(', ').trim();

  const options = {
    toastId: message,
    onOpen: () => {
      errorCounts[message] = 1;
    },
    onClose: () => {
      delete errorCounts[message];
    },
  };

  // Specific logic for timestamp mismatch:
  if (message === 'Timestamp mismatch') {
    if (message in errorCounts) {
      errorCounts[message] += 1;
      const count = errorCounts[message];
      toast.update(options.toastId, {
        render: getTimestampMismatchMessage(count),
      });
    }
    toast.error(getTimestampMismatchMessage(), options);
    return;
  }

  // Specific logic for invalid geometry type
  if (message.includes('Invalid geometry type')) {
    message = [message, INVALID_GEOMETRY_MESSAGE].join(' ');
  }

  // Logic for all other errors:
  // -> If this error is already shown in a toast, just update that toast with the count
  if (message in errorCounts) {
    // -> Modify the toast message to let the user know there have been multiple errors of this type:
    errorCounts[message] += 1;
    const count = errorCounts[message];
    toast.update(options.toastId, { render: `${message} (${count} errors)` });
    return;
  }
  // -> Otherwise, this error isn't already shown, so add a toast:
  toast.error(message, options);
};

/**
 * Display a warning toast.
 */
export const showWarning = (
  message: React.ReactNode,
  options: ToastOptions = { autoClose: 5000 },
) => {
  toast.warn(message, options);
};

/**
 * Display a success toast.
 */
export const showSuccess = (
  message: React.ReactNode,
  options: ToastOptions = { autoClose: 5000 },
) => {
  toast.success(message, options);
};

/**
 * Runs `mutate()` and displays errors if any occur. Re-throws errors. Does not
 * manage any state.
 */
export const mutateAndDisplayErrors = async <T,>(
  mutate: () => Promise<ExecutionResult<GraphQLData<T>>>,
  failureMessage: string = 'Update failed, please try again',
) => {
  try {
    const response = await mutate();
    const { result, errors } = Object.values(response.data || {})[0] || {};
    if (errors || !result) {
      const errorMessage = (errors || [failureMessage]).join(', ');
      throw new Error(errorMessage);
    }
    return result;
  } catch (err) {
    showError(err);
    throw err;
  }
};
